// app/_layout.tsx
// VERSION FINALE avec SubscriptionProvider + ATT

import FontAwesome from "@expo/vector-icons/FontAwesome";
import { DarkTheme, DefaultTheme, ThemeProvider } from "@react-navigation/native";
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import { useFonts } from "expo-font";
import * as Notifications from 'expo-notifications';
import { Stack } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { useEffect } from "react";
import { Platform } from "react-native";
import { MD3LightTheme, PaperProvider } from "react-native-paper";
import "react-native-reanimated";

// ✅ FIX: Import conditionnel pour le web
let getTrackingPermissionsAsync: any;
let requestTrackingPermissionsAsync: any;
if (Platform.OS !== 'web') {
  const mod = require('expo-tracking-transparency');
  getTrackingPermissionsAsync = mod.getTrackingPermissionsAsync;
  requestTrackingPermissionsAsync = mod.requestTrackingPermissionsAsync;
}

import { useColorScheme } from "@/components/useColorScheme";
import { configureRevenueCat, loginRevenueCat, logoutRevenueCat } from "@/lib/revenuecat";
import { supabase } from "@/lib/supabase";
import { AuthProvider } from "../contexts/AuthContext";
import { SubscriptionProvider } from "../contexts/SubscriptionContext";

export { ErrorBoundary } from "expo-router";

export const unstable_settings = {
  initialRouteName: "index",
};

// Configuration du thème Ernest
const ernestTheme = {
  ...MD3LightTheme,
  colors: {
    ...MD3LightTheme.colors,
    primary: '#6366F1',
    secondary: '#F59E0B',
    tertiary: '#7C3AED',
    background: '#F8FAFC',
    surface: '#FFFFFF',
    onPrimary: '#FFFFFF',
    onSecondary: '#FFFFFF',
    onBackground: '#0F172A',
    onSurface: '#64748B',
    error: '#F43F5E',
    outline: '#E2E8F0',
  },
};

const ErnestLightTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: '#F8FAFC',
    card: '#FFFFFF',
    text: '#0F172A',
    border: '#E2E8F0',
    primary: '#6366F1',
  },
};

const ErnestDarkTheme = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    background: '#0F172A',
    card: '#1E293B',
    text: '#F8FAFC',
    border: '#334155',
    primary: '#818CF8',
  },
};

// Configuration des notifications
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

// Fonction pour récupérer et mettre à jour le push token
async function updatePushToken(userId: string): Promise<void> {
  if (Platform.OS === 'web') return;
  
  if (!Device.isDevice) {
    console.log('Push notifications require a physical device');
    return;
  }

  try {
    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    
    if (existingStatus !== 'granted') {
      console.log('Push notification permission not granted');
      return;
    }

    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#6366F1',
      });
    }

    let token: string | null = null;
    const projectId = Constants.expoConfig?.extra?.eas?.projectId ?? Constants.easConfig?.projectId;
    
    if (projectId) {
      token = (await Notifications.getExpoPushTokenAsync({ projectId })).data;
    } else {
      token = (await Notifications.getExpoPushTokenAsync()).data;
    }

    if (!token) {
      console.log('Could not get push token');
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('push_token')
      .eq('id', userId)
      .single();

    if (profile?.push_token !== token) {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          push_token: token,
          notifications_enabled: true,
        })
        .eq('id', userId);

      if (error) {
        console.error('Error updating push token:', error);
      } else {
        console.log('✅ Push token updated successfully');
      }
    }
  } catch (error) {
    console.error('Error in updatePushToken:', error);
  }
}

SplashScreen.preventAutoHideAsync();

export default function RootLayout() {
  const [loaded, error] = useFonts({
    SpaceMono: require("../assets/fonts/SpaceMono-Regular.ttf"),
    ...FontAwesome.font,
  });

  useEffect(() => {
    if (error) throw error;
  }, [error]);

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();
    }
  }, [loaded]);

  if (!loaded) return null;

  return <RootLayoutNav />;
}

function RootLayoutNav() {
  const colorScheme = useColorScheme();

  // ✅ ATT - App Tracking Transparency (iOS uniquement)
  useEffect(() => {
    if (Platform.OS !== 'ios') return;

    const requestTracking = async () => {
      try {
        const { status } = await getTrackingPermissionsAsync();
        if (status === 'undetermined') {
          setTimeout(async () => {
            await requestTrackingPermissionsAsync();
          }, 1000);
        }
      } catch (e) {
        console.log('[ATT] Error:', e);
      }
    };

    requestTracking();
  }, []);

  useEffect(() => {
    const initApp = async () => {
      await configureRevenueCat();
      
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await loginRevenueCat(user.id);
        await updatePushToken(user.id);
      }
    };
    
    initApp();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        await loginRevenueCat(session.user.id);
        await updatePushToken(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        await logoutRevenueCat();
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // ✅ FIX: Utiliser .remove() au lieu de Notifications.removeNotificationSubscription()
  useEffect(() => {
    const notificationListener = Notifications.addNotificationReceivedListener(notification => {
      console.log('📬 Notification received:', notification);
    });

    const responseListener = Notifications.addNotificationResponseReceivedListener(response => {
      console.log('👆 Notification clicked:', response);
    });

    return () => {
      notificationListener.remove();
      responseListener.remove();
    };
  }, []);

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <PaperProvider theme={ernestTheme}>
          <ThemeProvider value={colorScheme === "dark" ? ErnestDarkTheme : ErnestLightTheme}>
            <Stack>
              <Stack.Screen name="index" options={{ headerShown: false }} />
              <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
              <Stack.Screen name="onboarding" options={{ headerShown: false }} />
              <Stack.Screen name="login" options={{ headerShown: false }} />
              <Stack.Screen name="signup" options={{ headerShown: false }} />
              <Stack.Screen name="paywall" options={{ headerShown: false }} />
              <Stack.Screen name="subscription-success" options={{ headerShown: false }} />
              <Stack.Screen name="reset-password" options={{ headerShown: false }} />
              <Stack.Screen name="modal" options={{ presentation: "modal" }} />
              <Stack.Screen name="notes/[id]" options={{ headerShown: false }} />
              <Stack.Screen name="quiz/[id]" options={{ headerShown: false }} />
              <Stack.Screen name="flashcards/[id]" options={{ headerShown: false }} />
              <Stack.Screen name="conversation/[id]" options={{ headerShown: false }} />
            </Stack>
          </ThemeProvider>
        </PaperProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}